import { connectStats } from "instantsearch.js/es/connectors";
import { getParam, setParam } from "../../../../js/utils/url-params.js";

/**
 * Builds the inline "for {query}" suffix appended to the result count.
 *
 * The query is only known client-side, so the template carries a `{{query}}`
 * token (rendered server-side via `|tc`) that is interpolated here. Returns an
 * empty string when there is no query or no template, so the suffix stays hidden
 * on the initial (query-less) render.
 * @param {string} query - The current search query.
 * @param {string} forTemplate - Localized template containing the `{{query}}` token.
 * @example
 * formatQuerySuffix("windows", 'for “{{query}}”'); // 'for “windows”'
 * @returns {string} The interpolated suffix, or an empty string.
 */
export const formatQuerySuffix = (query, forTemplate) => {
	const trimmedQuery = (query ?? "").trim();

	if (!trimmedQuery || !forTemplate) {
		return "";
	}

	return forTemplate.replace("{{query}}", trimmedQuery);
};

// for details, please review: https://www.algolia.com/doc/api-reference/widgets/stats/js/#create-a-render-function
const renderStats = (stats) => {
	const { nbHits, query, widgetParams } = stats;
	widgetParams.container.textContent = `${nbHits}`;

	const { queryContainer, forTemplate } = widgetParams;
	if (queryContainer) {
		queryContainer.textContent = formatQuerySuffix(query, forTemplate);
	}
};

const customStats = connectStats(renderStats);

/**
 * Initializes the custom stats widget for displaying the number of search results.
 * @param {string|HTMLElement} [statsContainer] - The container element or selector where the stats will be displayed. [Default: ".js-ResultBar-count"]
 * @param {object} [options] - Optional query-summary configuration.
 * @param {HTMLElement} [options.queryContainer] - Element that receives the inline "for {query}" suffix. Omit to render the count only (default behaviour).
 * @param {string} [options.forTemplate] - Localized template containing the `{{query}}` token, used to build the suffix.
 * @example
 * // Initialize the stats widget with a specific container
 * initStats('#my-stats-container');
 * @example
 * // Initialize the stats widget with the default container
 * initStats();
 * @example
 * // Also render the search term inline after the count
 * initStats(countEl, { queryContainer: termEl, forTemplate: 'for “{{query}}”' });
 * @returns {object} The custom stats widget instance.
 */
export const initStats = (
	statsContainer = ".js-ResultBar-count",
	{ queryContainer, forTemplate } = {},
) => {
	if (typeof statsContainer === "string") {
		statsContainer = document.querySelector(statsContainer);
	}

	// https://www.algolia.com/doc/api-reference/widgets/stats/js/
	return customStats({
		container: statsContainer,
		queryContainer,
		forTemplate,
	});
};

/**
 * Updates the search summary using the provided query string or a fallback.
 * @param {string} query - The search query.
 * @example
 * updateSearchSummary('windows');
 * @returns {void}
 */
export const updateSearchSummary = (query) => {
	const searchSummary = document.querySelector(".Resultbar-searchSummary");
	const searchSummaryTxt = searchSummary.querySelector("span");
	const summaryFallback = searchSummary.dataset.summaryFallback;

	searchSummaryTxt.textContent = query.trim() ? query : summaryFallback;
};

// View toggle is a presentational concern — it flips `data-view` on a target
// container so CSS can switch layouts. It deliberately does NOT participate in
// InstantSearch uiState / routing: there's no search-parameter change, no data
// fetch, and tangling it into InstantSearch's render lifecycle caused the
// state to be recomposed away on every render. The URL is updated directly
// via history.replaceState so the chosen view survives a reload or share,
// without colliding with InstantSearch's own routing for facet refinements.
const VIEW_QUERY_PARAM = "view";

/**
 * Wires the grid/list view toggle to a target container's `data-view`
 * attribute, restoring state from the URL on load and persisting changes via
 * `history.replaceState`.
 * @param {object} options
 * @param {string} options.targetSelector - CSS selector for the container whose `data-view` attribute the toggle controls (e.g. `"#product-listing-results"`).
 * @param {"grid" | "list"} [options.defaultView] - Fallback when the URL has no `view` param (default: `"grid"`).
 * @param {string} [options.toggleSelector] - CSS selector for the view toggle fieldset (default: `".ResultBar-viewToggle"`).
 * @param {string} [options.urlParam] - URL query-param key (default: `"view"`). Override only if a host page already uses `view` for something else.
 * @returns {{ destroy: () => void } | null} A handle for tearing down the
 *   listener (returns `null` if the toggle or target is missing in the DOM).
 */
export const initViewToggle = ({
	targetSelector,
	defaultView = "grid",
	toggleSelector = ".ResultBar-viewToggle",
	urlParam = VIEW_QUERY_PARAM,
}) => {
	if (!targetSelector) {
		throw new Error("[result-bar/initViewToggle] targetSelector is required");
	}

	const toggleElement = document.querySelector(toggleSelector);
	const targetElement = /** @type {HTMLElement | null} */ (
		document.querySelector(targetSelector)
	);
	if (!toggleElement || !targetElement) {
		return null;
	}

	/** @param {"grid" | "list"} view */
	const applyViewToDom = (view) => {
		targetElement.dataset.view = view;
		const matchingRadio = /** @type {HTMLInputElement | null} */ (
			toggleElement.querySelector(`[type="radio"][value="${view}"]`)
		);
		if (matchingRadio && !matchingRadio.checked) {
			matchingRadio.checked = true;
		}
	};

	/**
	 * @param {string | null} value
	 * @returns {"grid" | "list"}
	 */
	const normalizeView = (value) =>
		value === "grid" || value === "list" ? value : defaultView;

	const initialView = normalizeView(getParam(urlParam));
	applyViewToDom(initialView);

	const abortController = new AbortController();
	toggleElement.addEventListener(
		"change",
		(event) => {
			const target = /** @type {HTMLInputElement} */ (event.target);
			if (target.type !== "radio") {
				return;
			}
			const view = normalizeView(target.value);
			applyViewToDom(view);
			setParam(urlParam, view);
		},
		{ signal: abortController.signal },
	);

	return {
		destroy() {
			abortController.abort();
		},
	};
};
